/**
 * Shared "unsaved changes" guard for data-entry dialogs.
 *
 * Office-UX requirement: when the operator clicks the X close button or
 * the "Annulla" button on a form with unsaved data, ask for explicit
 * confirmation before discarding their work. The dialog itself prevents
 * outside-click / Escape close (see `DialogContent` in
 * `/components/ui/dialog.jsx`), so the only ways to close are this guard's
 * code paths.
 *
 * Usage:
 *   const isDirty = useFormDirty(formState, initialState);
 *   <Dialog open={open} onOpenChange={(o) => {
 *     if (!o && !confirmDirtyClose(isDirty)) return;
 *     setOpen(o);
 *   }}>
 *
 * The hook does a JSON-shallow compare against the snapshot taken at the
 * first call. The snapshot is refreshed automatically when `resetKey`
 * changes (typically the dialog `open` state) so re-opening the dialog
 * for a new record starts fresh.
 */
import { useRef, useEffect } from "react";

export const DIRTY_CONFIRM_MESSAGE =
  "Ci sono dati non salvati. Vuoi davvero chiudere?";

/**
 * Returns `true` when the user confirms (or when there are no unsaved
 * changes), `false` to abort the close.
 */
export function confirmDirtyClose(isDirty) {
  if (!isDirty) return true;
  // eslint-disable-next-line no-alert
  return window.confirm(DIRTY_CONFIRM_MESSAGE);
}

/**
 * Track whether the supplied form state has diverged from the snapshot
 * taken shortly after the most recent `resetKey` change.
 *
 * The snapshot is deferred by one macrotask so that open-time useEffects
 * (e.g. auto-fetch invoice number, default importoLordo, hydrate manual
 * rows from an existing record) have already settled by the time we
 * snapshot the "pristine" form. Without this delay every dialog would
 * appear dirty the moment it opens.
 *
 * @param {object} formState  Current form values (any shape).
 * @param {any}    resetKey   When this transitions to a truthy value the
 *                            snapshot is (re)taken (typically the dialog
 *                            `open` boolean).
 * @returns {boolean}         True when the form differs from the snapshot.
 */
export function useFormDirty(formState, resetKey) {
  const snapshotRef = useRef(null);
  const latestRef = useRef(formState);
  latestRef.current = formState;

  useEffect(() => {
    if (!resetKey) {
      snapshotRef.current = null;
      return undefined;
    }
    // Defer snapshot until the dialog's own open-time useEffects have
    // run. A single setTimeout(0) is enough in practice: it puts us in
    // the next macrotask, after the React commit + all the chained
    // setStates resolved.
    const handle = setTimeout(() => {
      snapshotRef.current = JSON.stringify(latestRef.current);
    }, 0);
    return () => clearTimeout(handle);
  }, [resetKey]);

  if (snapshotRef.current == null) return false;
  return JSON.stringify(formState) !== snapshotRef.current;
}
